import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase-config';
import { FiArrowRight, FiCheckCircle, FiArrowLeft, FiClock, FiAlertTriangle, FiCopy, FiCheck, FiSmartphone, FiHome, FiCreditCard, FiDownload, FiList, FiRefreshCw, FiUpload } from 'react-icons/fi';
import { generateReceipt } from '../utils/receiptGenerator';

const RATE_LOCK_SECONDS = 15 * 60; // 15 minutes

/* ─── Benin Banks ─── */
const BENIN_BANKS = [
  'Bank of Africa (BOA)',
  'Ecobank Bénin',
  'Société Générale Bénin',
  'NSIA Banque Bénin',
  'Orabank Bénin',
  'Diamond Bank Bénin',
  'Banque Atlantique Bénin',
  'UBA Bénin',
  'BGFI Bank Bénin',
  'Coris Bank Bénin',
  'Banque Sahélo-Saharienne (BSIC)',
  'La Poste du Bénin (CCP)',
];

/* ─── Benin Mobile Money Providers ─── */
const BENIN_MOMO_PROVIDERS = [
  { id: 'mtn_momo', label: 'MTN MoMo' },
  { id: 'moov_money', label: 'Moov Money' },
  { id: 'celtiis_cash', label: 'Celtiis Cash' },
];

/* ─── Cash Pickup Details ─── */
const CASH_PICKUP = {
  address: 'Cité Vie Nouvelle, close to Proci Morgue',
  phone: '0161215834',
};

const STEPS = [
  { num: 1, label: 'Amount' },
  { num: 2, label: 'Recipient' },
  { num: 3, label: 'Review' },
  { num: 4, label: 'Done' },
];

const formatCurrency = (amount, currency) => {
  if (amount == null || isNaN(amount)) return '—';
  const fmt = Number(amount).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  if (currency === 'NGN') return '₦' + fmt;
  if (currency === 'CFA' || currency === 'XOF') return 'CFA ' + fmt;
  return fmt;
};

const formatCountdown = (s) => {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${sec.toString().padStart(2, '0')}`;
};

/* ─── Step Indicator ─── */
function StepIndicator({ current }) {
  return (
    <div className="flex items-center justify-between mb-8">
      {STEPS.map((s, i) => {
        const isDone = current > s.num;
        const isActive = current === s.num;
        return (
          <React.Fragment key={s.num}>
            <div className="flex flex-col items-center gap-1.5">
              <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${
                isDone ? 'bg-status-green text-white' :
                isActive ? 'bg-primary text-white' :
                'bg-gray-100 text-gray-400'
              }`}>
                {isDone ? <FiCheck size={16} /> : s.num}
              </div>
              <span className={`text-[10px] sm:text-xs font-medium ${
                isActive ? 'text-primary' : isDone ? 'text-status-green' : 'text-gray-400'
              }`}>
                {s.label}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div className={`flex-1 h-0.5 mx-1 sm:mx-2 transition-colors ${
                current > s.num ? 'bg-status-green' : 'bg-gray-200'
              }`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

/* ─── Info Row ─── */
function InfoRow({ label, value, highlight, mono }) {
  return (
    <div className="flex justify-between items-center">
      <span className="text-xs text-text-secondary">{label}</span>
      <span className={`text-sm font-medium ${highlight ? 'text-status-yellow' : 'text-text-main'} ${mono ? 'font-mono' : ''}`}>{value}</span>
    </div>
  );
}

/* ═══════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════ */
export default function CreateTransaction({ merchantData }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);
  const [copied, setCopied] = useState(false);
  const [proofUploading, setProofUploading] = useState(false);
  const [proofUploaded, setProofUploaded] = useState(false);

  // Rate lock countdown
  const [rateLockRemaining, setRateLockRemaining] = useState(RATE_LOCK_SECONDS);
  const rateLockStarted = useRef(false);
  const timerRef = useRef(null);

  const [form, setForm] = useState({
    amount: '',
    inputCurrency: 'NGN',
    outputCurrency: 'CFA',
    settlementDirection: 'NGN_FIRST',
    payoutMethod: 'mobile_money', // mobile_money | bank_transfer | cash_pickup (CFA only)
    recipientName: '',
    recipientBank: '',
    recipientAccount: '',
    recipientPhone: '',
  });

  const fetchRates = useCallback(async () => {
    try {
      const fns = getFunctions();
      const merchantApi = httpsCallable(fns, 'merchantApi');
      const result = await merchantApi({ action: 'getMerchantRates' });
      setRates(result.data.rates || []);
    } catch (err) {
      console.error('Failed to load rates:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRates(); }, [fetchRates]);

  // Cleanup timer on unmount
  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);

  // Find applicable rate
  const currentRate = useMemo(() => {
    const from = form.inputCurrency === 'XOF' ? 'CFA' : form.inputCurrency;
    const to = form.outputCurrency === 'XOF' ? 'CFA' : form.outputCurrency;
    const direct = rates.find(r => r.from === from && r.to === to);
    if (direct) return direct.rate;
    const inverse = rates.find(r => r.from === to && r.to === from);
    if (inverse && inverse.rate > 0) return 1 / inverse.rate;
    return null;
  }, [rates, form.inputCurrency, form.outputCurrency]);

  const outputAmount = useMemo(() => {
    const amt = parseFloat(form.amount);
    if (isNaN(amt) || amt <= 0 || !currentRate) return null;
    return Math.round(amt * currentRate);
  }, [form.amount, currentRate]);

  const handleField = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const swapCurrencies = () => {
    setForm(prev => ({
      ...prev,
      inputCurrency: prev.outputCurrency,
      outputCurrency: prev.inputCurrency,
      settlementDirection: prev.inputCurrency === 'CFA' ? 'CFA_FIRST' : 'NGN_FIRST',
    }));
  };

  /* ─── Step transitions ─── */
  const goStep1Next = () => {
    setError('');
    const amt = parseFloat(form.amount);
    if (isNaN(amt) || amt <= 0) { setError('Enter a valid amount'); return; }
    if (!currentRate) { setError('Exchange rate is currently unavailable. Please try again shortly.'); return; }
    setStep(2);
  };

  const goStep2Next = () => {
    setError('');
    if (form.outputCurrency === 'NGN') {
      // NGN payout: bank transfer only
      if (!form.recipientName.trim()) { setError('Recipient name is required'); return; }
      if (!form.recipientBank.trim()) { setError('Bank name is required'); return; }
      if (!form.recipientAccount.trim()) { setError('Account number is required'); return; }
    } else if (form.payoutMethod === 'mobile_money') {
      if (!form.recipientName.trim()) { setError('Recipient name is required'); return; }
      if (!form.recipientBank.trim()) { setError('Mobile money provider is required'); return; }
      if (!form.recipientPhone.trim()) { setError('Phone number is required'); return; }
    } else if (form.payoutMethod === 'bank_transfer') {
      if (!form.recipientName.trim()) { setError('Account holder name is required'); return; }
      if (!form.recipientBank.trim()) { setError('Bank is required'); return; }
      if (!form.recipientAccount.trim()) { setError('Account number is required'); return; }
    }
    // cash_pickup needs no extra fields
    // Start the rate lock countdown
    if (!rateLockStarted.current) {
      rateLockStarted.current = true;
      setRateLockRemaining(RATE_LOCK_SECONDS);
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = setInterval(() => {
        setRateLockRemaining(prev => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    setStep(3);
  };

  const handleConfirmSubmit = async () => {
    if (rateLockRemaining <= 0) {
      setError('Rate has expired. Go back to refresh the rate.');
      return;
    }
    try {
      setSubmitting(true);
      setError('');
      const fns = getFunctions();
      const merchantApi = httpsCallable(fns, 'merchantApi');
      const payoutMethodMap = { mobile_money: 'MOBILE_MONEY', bank_transfer: 'BANK', cash_pickup: 'CASH_PICKUP' };
      const result = await merchantApi({
        action: 'createMerchantTransaction',
        ...form,
        amount: parseFloat(form.amount),
        payoutMethod: payoutMethodMap[form.payoutMethod] || form.payoutMethod,
      });
      if (timerRef.current) clearInterval(timerRef.current);
      setSuccess(result.data);
      setStep(4);
    } catch (err) {
      const msg = err?.message || 'Failed to create transaction';
      setError(msg.replace('Firebase: ', ''));
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoBack = (toStep) => {
    setError('');
    setStep(toStep);
  };

  const resetAll = () => {
    setStep(1);
    setSuccess(null);
    setError('');
    setProofUploaded(false);
    rateLockStarted.current = false;
    if (timerRef.current) clearInterval(timerRef.current);
    setRateLockRemaining(RATE_LOCK_SECONDS);
    setForm({ amount: '', inputCurrency: 'NGN', outputCurrency: 'CFA', settlementDirection: 'NGN_FIRST', payoutMethod: 'mobile_money', recipientName: '', recipientBank: '', recipientAccount: '', recipientPhone: '' });
  };

  const handleProofUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !success?.transactionId || proofUploading || proofUploaded) return;
    if (file.size > 10 * 1024 * 1024) { setError('File too large. Max 10MB.'); return; }
    try {
      setProofUploading(true);
      setError('');
      const ext = file.name.split('.').pop();
      const path = `proof_of_payment/${success.transactionId}_${Date.now()}.${ext}`;
      const storageRef = ref(storage, path);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      const fns = getFunctions();
      const merchantApi = httpsCallable(fns, 'merchantApi');
      await merchantApi({ action: 'uploadProofOfPayment', transactionId: success.transactionId, proofOfPayment: url });
      setProofUploaded(true);
    } catch (err) {
      console.error('Upload failed:', err);
      setError(err?.message?.includes('storage') || err?.code?.includes('storage')
        ? 'Upload failed. Please check your connection and try again.'
        : 'Failed to upload proof of payment. Please try again.');
    } finally {
      setProofUploading(false);
    }
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const rateDisplay = currentRate
    ? `1 ${form.inputCurrency} = ${currentRate < 1 ? currentRate.toFixed(4) : currentRate.toFixed(2)} ${form.outputCurrency}`
    : null;

  /* ═══════════════════════════════════════════
     RENDER
     ═══════════════════════════════════════════ */
  return (
    <div className="max-w-xl mx-auto">
      {/* Step indicator (always visible) */}
      <StepIndicator current={step} />

      {/* ── STEP 1: SELECT TRANSACTION ── */}
      {step === 1 && (
        <div className="bg-surface rounded-xl shadow-card p-5 sm:p-6">
          <h2 className="text-lg font-bold text-text-main mb-1">Select Transaction</h2>
          <p className="text-xs text-text-secondary mb-5">Choose transaction type and enter amount</p>

          <div className="space-y-5">
            {/* Transaction Type */}
            <div>
              <label className="block text-xs font-medium text-text-secondary mb-2">Transaction Type</label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => { handleField('inputCurrency', 'NGN'); handleField('outputCurrency', 'CFA'); handleField('settlementDirection', 'NGN_FIRST'); }}
                  className={`p-4 rounded-xl border-2 text-center transition-all ${
                    form.inputCurrency === 'NGN'
                      ? 'border-primary bg-primary/5'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <span className="text-lg font-bold text-text-main">NGN → CFA</span>
                  <p className="text-[10px] text-text-secondary mt-0.5">Send Naira, receive CFA</p>
                </button>
                <button
                  type="button"
                  onClick={() => { handleField('inputCurrency', 'CFA'); handleField('outputCurrency', 'NGN'); handleField('settlementDirection', 'CFA_FIRST'); }}
                  className={`p-4 rounded-xl border-2 text-center transition-all ${
                    form.inputCurrency === 'CFA'
                      ? 'border-primary bg-primary/5'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <span className="text-lg font-bold text-text-main">CFA → NGN</span>
                  <p className="text-[10px] text-text-secondary mt-0.5">Send CFA, receive Naira</p>
                </button>
              </div>
            </div>

            {/* Amount */}
            <div>
              <label className="block text-xs font-medium text-text-secondary mb-1.5">You Send</label>
              <div className="relative">
                <input
                  type="number"
                  value={form.amount}
                  onChange={(e) => handleField('amount', e.target.value)}
                  placeholder="0"
                  min="0"
                  step="1"
                  className="w-full px-4 py-3.5 pr-16 text-xl font-semibold rounded-xl border border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm font-bold text-text-secondary">
                  {form.inputCurrency}
                </span>
              </div>
            </div>

            {/* Live rate & output */}
            {currentRate && form.amount && outputAmount != null && (
              <div className="bg-background rounded-xl p-4 space-y-2">
                <div className="flex items-center justify-between text-xs text-text-secondary">
                  <span>Exchange Rate</span>
                  <span className="font-medium text-text-main">{rateDisplay}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-text-secondary">You Receive</span>
                  <span className="text-lg font-bold text-primary">{formatCurrency(outputAmount, form.outputCurrency)}</span>
                </div>
              </div>
            )}

            {!currentRate && !loading && (
              <div className="p-3 bg-status-red-bg rounded-xl text-xs text-status-red text-center">
                Exchange rate unavailable for this pair. Please try again or contact support.
              </div>
            )}

            {error && <div className="p-3 bg-status-red-bg text-status-red text-sm rounded-lg">{error}</div>}

            <button
              type="button"
              onClick={goStep1Next}
              disabled={loading || !currentRate}
              className="w-full py-3.5 bg-primary text-white rounded-xl font-semibold text-sm hover:bg-primary-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Continue
            </button>
          </div>
        </div>
      )}

      {/* ── STEP 2: RECIPIENT DETAILS ── */}
      {step === 2 && (
        <div className="bg-surface rounded-xl shadow-card p-5 sm:p-6">
          <h2 className="text-lg font-bold text-text-main mb-1">Recipient Details</h2>
          <p className="text-xs text-text-secondary mb-5">
            {form.outputCurrency === 'NGN'
              ? 'Enter the Nigerian bank account to receive Naira'
              : 'How should the recipient receive CFA?'}
          </p>

          <div className="space-y-4">

            {/* ── CFA payout method selector ── */}
            {form.outputCurrency !== 'NGN' && (
              <div>
                <label className="block text-xs font-medium text-text-secondary mb-2">Payout Method</label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { id: 'mobile_money', label: 'Mobile Money', icon: <FiSmartphone size={18} /> },
                    { id: 'bank_transfer', label: 'Bank Transfer', icon: <FiCreditCard size={18} /> },
                    { id: 'cash_pickup', label: 'Cash Pickup', icon: <FiHome size={18} /> },
                  ].map(m => (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => { handleField('payoutMethod', m.id); handleField('recipientBank', ''); handleField('recipientAccount', ''); handleField('recipientPhone', ''); handleField('recipientName', ''); }}
                      className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-all text-center ${
                        form.payoutMethod === m.id
                          ? 'border-primary bg-primary/5 text-primary'
                          : 'border-gray-200 text-text-secondary hover:border-gray-300'
                      }`}
                    >
                      {m.icon}
                      <span className="text-[10px] sm:text-xs font-semibold leading-tight">{m.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* ── NGN payout (always bank) ── */}
            {form.outputCurrency === 'NGN' && (
              <>
                <div>
                  <label className="block text-xs font-medium text-text-secondary mb-1.5">Recipient Name</label>
                  <input
                    type="text"
                    value={form.recipientName}
                    onChange={(e) => handleField('recipientName', e.target.value)}
                    placeholder="Full name"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-text-secondary mb-1.5">Bank Name</label>
                  <input
                    type="text"
                    value={form.recipientBank}
                    onChange={(e) => handleField('recipientBank', e.target.value)}
                    placeholder="e.g. GTBank, UBA, First Bank"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-text-secondary mb-1.5">Account Number</label>
                  <input
                    type="text"
                    value={form.recipientAccount}
                    onChange={(e) => handleField('recipientAccount', e.target.value)}
                    placeholder="10-digit account number"
                    maxLength={10}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none text-sm font-mono"
                  />
                </div>
              </>
            )}

            {/* ── CFA: Mobile Money ── */}
            {form.outputCurrency !== 'NGN' && form.payoutMethod === 'mobile_money' && (
              <>
                <div>
                  <label className="block text-xs font-medium text-text-secondary mb-1.5">Network Provider</label>
                  <select
                    value={form.recipientBank}
                    onChange={(e) => handleField('recipientBank', e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none text-sm"
                  >
                    <option value="">Select provider</option>
                    {BENIN_MOMO_PROVIDERS.map(p => (
                      <option key={p.id} value={p.label}>{p.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-text-secondary mb-1.5">Recipient Name</label>
                  <input
                    type="text"
                    value={form.recipientName}
                    onChange={(e) => handleField('recipientName', e.target.value)}
                    placeholder="Full name on mobile money account"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-text-secondary mb-1.5">Phone Number</label>
                  <input
                    type="tel"
                    value={form.recipientPhone}
                    onChange={(e) => handleField('recipientPhone', e.target.value)}
                    placeholder="e.g. +229 97 12 34 56"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none text-sm font-mono"
                  />
                </div>
              </>
            )}

            {/* ── CFA: Bank Transfer ── */}
            {form.outputCurrency !== 'NGN' && form.payoutMethod === 'bank_transfer' && (
              <>
                <div>
                  <label className="block text-xs font-medium text-text-secondary mb-1.5">Bank</label>
                  <select
                    value={form.recipientBank}
                    onChange={(e) => handleField('recipientBank', e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none text-sm"
                  >
                    <option value="">Select bank</option>
                    {BENIN_BANKS.map(b => (
                      <option key={b} value={b}>{b}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-text-secondary mb-1.5">Account Holder Name</label>
                  <input
                    type="text"
                    value={form.recipientName}
                    onChange={(e) => handleField('recipientName', e.target.value)}
                    placeholder="Full name on bank account"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-text-secondary mb-1.5">Account Number</label>
                  <input
                    type="text"
                    value={form.recipientAccount}
                    onChange={(e) => handleField('recipientAccount', e.target.value)}
                    placeholder="Bank account number"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none text-sm font-mono"
                  />
                </div>
              </>
            )}

            {/* ── CFA: Cash Pickup ── */}
            {form.outputCurrency !== 'NGN' && form.payoutMethod === 'cash_pickup' && (
              <div className="bg-background rounded-xl p-4 space-y-3">
                <div className="flex items-start gap-3">
                  <FiHome className="text-primary mt-0.5 flex-shrink-0" size={20} />
                  <div>
                    <p className="text-sm font-semibold text-text-main mb-1">Pickup Location</p>
                    <p className="text-sm text-text-secondary">{CASH_PICKUP.address}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <FiSmartphone className="text-primary mt-0.5 flex-shrink-0" size={20} />
                  <div>
                    <p className="text-sm font-semibold text-text-main mb-1">Contact for Directions</p>
                    <p className="text-sm text-text-secondary font-mono">{CASH_PICKUP.phone}</p>
                  </div>
                </div>
                <div className="bg-status-yellow-bg rounded-lg p-3 mt-2">
                  <p className="text-xs text-status-yellow font-medium">
                    Please bring a valid ID when collecting cash. You will be notified when funds are ready.
                  </p>
                </div>
              </div>
            )}

            {error && <div className="p-3 bg-status-red-bg text-status-red text-sm rounded-lg">{error}</div>}

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={() => handleGoBack(1)}
                className="flex-1 py-3 border border-gray-200 text-text-main rounded-xl text-sm font-semibold hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
              >
                <FiArrowLeft size={16} /> Back
              </button>
              <button
                type="button"
                onClick={goStep2Next}
                className="flex-1 py-3 bg-primary text-white rounded-xl text-sm font-semibold hover:bg-primary-light transition-colors"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── STEP 3: REVIEW & CONFIRM ── */}
      {step === 3 && (
        <div className="bg-surface rounded-xl shadow-card p-5 sm:p-6">
          <h2 className="text-lg font-bold text-text-main mb-1">Review & Confirm</h2>
          <p className="text-xs text-text-secondary mb-5">Verify all details before confirming</p>

          {/* Rate lock countdown */}
          <div className={`flex items-center gap-2 px-4 py-2.5 rounded-xl mb-5 ${
            rateLockRemaining <= 60 ? 'bg-status-red-bg' : 'bg-status-yellow-bg'
          }`}>
            <FiClock size={16} className={rateLockRemaining <= 60 ? 'text-status-red' : 'text-status-yellow'} />
            <span className={`text-xs font-medium ${rateLockRemaining <= 60 ? 'text-status-red' : 'text-status-yellow'}`}>
              {rateLockRemaining > 0
                ? `Rate expires in ${formatCountdown(rateLockRemaining)}`
                : 'Rate has expired — go back to refresh'}
            </span>
          </div>

          {/* Full summary */}
          <div className="bg-background rounded-xl p-4 space-y-3 mb-5">
            <InfoRow label="Transaction Type" value={`${form.inputCurrency} → ${form.outputCurrency}`} />
            <InfoRow label="You Send" value={formatCurrency(parseFloat(form.amount), form.inputCurrency)} />
            <InfoRow label="Exchange Rate" value={rateDisplay} />
            <InfoRow label="You Receive" value={formatCurrency(outputAmount, form.outputCurrency)} />
            <div className="border-t border-gray-200 my-1" />

            {/* NGN bank payout */}
            {form.outputCurrency === 'NGN' && (
              <>
                <InfoRow label="Payout Method" value="Bank Transfer" />
                <InfoRow label="Recipient" value={form.recipientName} />
                <InfoRow label="Bank" value={form.recipientBank} />
                <InfoRow label="Account Number" value={form.recipientAccount} mono />
              </>
            )}

            {/* CFA mobile money */}
            {form.outputCurrency !== 'NGN' && form.payoutMethod === 'mobile_money' && (
              <>
                <InfoRow label="Payout Method" value="Mobile Money" />
                <InfoRow label="Provider" value={form.recipientBank} />
                <InfoRow label="Recipient" value={form.recipientName} />
                <InfoRow label="Phone Number" value={form.recipientPhone} mono />
              </>
            )}

            {/* CFA bank transfer */}
            {form.outputCurrency !== 'NGN' && form.payoutMethod === 'bank_transfer' && (
              <>
                <InfoRow label="Payout Method" value="Bank Transfer" />
                <InfoRow label="Bank" value={form.recipientBank} />
                <InfoRow label="Account Holder" value={form.recipientName} />
                <InfoRow label="Account Number" value={form.recipientAccount} mono />
              </>
            )}

            {/* CFA cash pickup */}
            {form.outputCurrency !== 'NGN' && form.payoutMethod === 'cash_pickup' && (
              <>
                <InfoRow label="Payout Method" value="Cash Pickup" />
                <InfoRow label="Location" value={CASH_PICKUP.address} />
                <InfoRow label="Contact" value={CASH_PICKUP.phone} mono />
              </>
            )}
          </div>

          {error && <div className="p-3 bg-status-red-bg text-status-red text-sm rounded-lg mb-4">{error}</div>}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => handleGoBack(2)}
              disabled={submitting}
              className="flex-1 py-3 border border-gray-200 text-text-main rounded-xl text-sm font-semibold hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
            >
              <FiArrowLeft size={16} /> Back
            </button>
            <button
              type="button"
              onClick={handleConfirmSubmit}
              disabled={submitting || rateLockRemaining <= 0}
              className="flex-1 py-3 bg-primary text-white rounded-xl text-sm font-semibold hover:bg-primary-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Submitting...' : 'Confirm Transaction'}
            </button>
          </div>
        </div>
      )}

      {/* ── STEP 4: TRANSACTION SUBMITTED ── */}
      {step === 4 && success && (
        <div className="space-y-5">
          {/* Success banner — automated feel */}
          <div className="bg-surface rounded-xl shadow-card p-6 text-center">
            <div className="w-16 h-16 bg-status-green-bg rounded-full flex items-center justify-center mx-auto mb-4">
              <FiCheckCircle className="text-status-green" size={32} />
            </div>
            <h2 className="text-xl font-bold text-text-main mb-1">Transaction Submitted</h2>
            <p className="text-sm text-text-secondary">
              Your transaction has been received. Processing will begin once your payment is confirmed.
            </p>
          </div>

          {/* Estimated timeline */}
          <div className="bg-surface rounded-xl shadow-card p-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                <FiClock className="text-primary" size={20} />
              </div>
              <div>
                <h3 className="text-sm font-bold text-text-main">Estimated Completion</h3>
                <p className="text-xs text-text-secondary">0 – 6 hours</p>
              </div>
            </div>
            <div className="space-y-0">
              {[
                { label: 'Payment Confirmation', time: '0 – 30 min', done: false, active: true },
                { label: 'Processing Transfer', time: '30 min – 2 hrs', done: false, active: false },
                { label: 'Completing Delivery', time: '2 – 6 hrs', done: false, active: false },
              ].map((s, i) => (
                <div key={i} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs ${
                      s.active ? 'bg-primary text-white' : 'bg-gray-100 text-gray-400'
                    }`}>
                      {i + 1}
                    </div>
                    {i < 2 && <div className="w-0.5 h-5 bg-gray-200" />}
                  </div>
                  <div className="pb-2">
                    <p className={`text-sm font-medium ${s.active ? 'text-text-main' : 'text-gray-400'}`}>{s.label}</p>
                    <p className="text-[10px] text-text-secondary">{s.time}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Deposit instructions — only for NGN input */}
          {success.inputCurrency === 'NGN' && (
            <div className="bg-surface rounded-xl shadow-card p-5">
              <div className="flex items-center gap-2 mb-4">
                <FiAlertTriangle className="text-status-yellow" size={18} />
                <h3 className="text-sm font-bold text-text-main">Payment Instructions</h3>
              </div>
              <p className="text-sm text-text-secondary mb-3">
                Make a bank transfer of <strong className="text-text-main">{formatCurrency(success.amount, 'NGN')}</strong> using the details below:
              </p>
              <div className="bg-background rounded-xl p-4 space-y-2.5">
                <InfoRow label="Bank Name" value="Kuda Bank" />
                <InfoRow label="Account Name" value="Crownphix Technologies" />
                <div className="flex justify-between items-center">
                  <span className="text-xs text-text-secondary">Account Number</span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-primary font-mono">3003235745</span>
                    <button onClick={() => handleCopy('3003235745')} className="touch-target text-gray-400 hover:text-primary transition-colors">
                      {copied ? <FiCheck size={16} className="text-status-green" /> : <FiCopy size={16} />}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {success.inputCurrency !== 'NGN' && (
            <div className="bg-surface rounded-xl shadow-card p-5">
              <div className="flex items-center gap-2 mb-3">
                <FiAlertTriangle className="text-status-yellow" size={18} />
                <h3 className="text-sm font-bold text-text-main">Payment Instructions</h3>
              </div>
              <p className="text-sm text-text-secondary">
                Make a payment of <strong className="text-text-main">{formatCurrency(success.amount, 'CFA')}</strong> via your CFA payment method. Details will be sent to your email.
              </p>
            </div>
          )}

          {/* Upload proof of payment */}
          <div className="bg-surface rounded-xl shadow-card p-5">
            <div className="flex items-center gap-2 mb-3">
              <FiUpload className={proofUploaded ? 'text-status-green' : 'text-primary'} size={18} />
              <h3 className="text-sm font-bold text-text-main">
                {proofUploaded ? 'Proof Uploaded' : 'Upload Transfer Receipt'}
              </h3>
            </div>
            {proofUploaded ? (
              <div className="flex items-center gap-2 bg-status-green-bg rounded-lg p-3">
                <FiCheckCircle className="text-status-green" size={16} />
                <p className="text-sm text-status-green font-medium">Your proof of payment has been submitted for verification.</p>
              </div>
            ) : (
              <>
                <p className="text-sm text-text-secondary mb-4">
                  After completing your transfer, upload a screenshot or photo of the receipt to speed up payment confirmation.
                </p>
                <label className={`flex flex-col items-center justify-center w-full py-6 border-2 border-dashed rounded-xl cursor-pointer transition-colors ${
                  proofUploading ? 'border-gray-200 bg-gray-50' : 'border-primary/30 hover:border-primary hover:bg-primary/5'
                }`}>
                  <input
                    type="file"
                    accept="image/*,.pdf"
                    onChange={handleProofUpload}
                    disabled={proofUploading}
                    className="hidden"
                  />
                  {proofUploading ? (
                    <div className="flex flex-col items-center gap-2">
                      <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                      <span className="text-sm text-text-secondary">Uploading...</span>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2">
                      <FiUpload className="text-primary" size={24} />
                      <span className="text-sm font-medium text-primary">Tap to upload receipt</span>
                      <span className="text-[10px] text-text-secondary">JPG, PNG or PDF • Max 10MB</span>
                    </div>
                  )}
                </label>
              </>
            )}
          </div>

          {/* Transaction summary */}
          <div className="bg-surface rounded-xl shadow-card p-5">
            <h3 className="text-sm font-bold text-text-main mb-3">Transaction Summary</h3>
            <div className="bg-background rounded-xl p-4 space-y-2.5">
              <div className="flex justify-between items-center">
                <span className="text-xs text-text-secondary">Reference</span>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold text-text-main font-mono">{success.reference}</span>
                  <button onClick={() => handleCopy(success.reference)} className="touch-target text-gray-400 hover:text-primary transition-colors">
                    <FiCopy size={14} />
                  </button>
                </div>
              </div>
              <InfoRow label="You Send" value={formatCurrency(success.amount, success.inputCurrency)} />
              <InfoRow label="You Receive" value={formatCurrency(success.outputAmount, success.outputCurrency)} />
              <InfoRow label="Rate" value={`1 ${success.inputCurrency} = ${success.rate} ${success.outputCurrency}`} />
            </div>
          </div>

          {/* Info banner */}
          <div className="bg-primary/5 border border-primary/10 rounded-xl p-4">
            <div className="flex items-center gap-2 justify-center mb-1">
              <p className="text-xs text-primary font-semibold">Processed securely by KwikX</p>
            </div>
            <p className="text-xs text-primary/70 font-medium text-center leading-relaxed">
              You'll receive a notification at each step. Track your transaction anytime from the Transactions page.
            </p>
          </div>

          {/* Action buttons */}
          <div className="space-y-3">
            <button
              onClick={() => navigate(`/transactions/${success.transactionId}`)}
              className="w-full py-3.5 bg-primary text-white rounded-xl text-sm font-semibold hover:bg-primary-light transition-colors flex items-center justify-center gap-2"
            >
              <FiList size={16} />
              Track Transaction
            </button>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => generateReceipt({ ...success, status: 'AWAITING_FUNDS', payoutMethod: form.payoutMethod, recipientName: form.recipientName, recipientBank: form.recipientBank, recipientAccount: form.recipientAccount, recipientPhone: form.recipientPhone })}
                className="py-3 border border-gray-200 text-text-main rounded-xl text-sm font-semibold hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
              >
                <FiDownload size={16} />
                Receipt
              </button>
              <button
                onClick={() => navigate('/')}
                className="py-3 border border-gray-200 text-text-main rounded-xl text-sm font-semibold hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
              >
                <FiHome size={16} />
                Go Home
              </button>
            </div>
            <button
              onClick={resetAll}
              className="w-full py-3 text-text-secondary text-sm font-medium hover:text-primary transition-colors flex items-center justify-center gap-2"
            >
              <FiRefreshCw size={14} />
              New Transaction
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
